import raygun4js from "raygun4js";
import React, { type ReactNode } from "react";
import reactStringReplace from "react-string-replace";
import type { DocumentSubType, Party } from "@/briefpoint-client";
import DefinitionHighlightPropound from "@/components/PropoundDefinitions/DefinitionHighlightPropound";
import type { CaseResponse, DefinitionItem } from "@/lib/types";

// Explicitly check to see if a string is null, undefined, or an empty string
export function isStringIsNullOrEmpty(value: string | null | undefined) {
  return value === undefined || value === null || value === "";
}

// strip <script> tags from strings containing HTML
export function sanitizeHtmlString(text?: string | null) {
  if (!text) {
    return "";
  }
  return text.replace(/<\/?script>/gi, "");
}

export const validateEmail = (email: string) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// TODO: make sure instances of this have the correct order of parameters
export async function promiseRetry<T>(
  fn: () => Promise<T>,
  retryIntervalMillis: number,
  retries = 5,
  previousError?: Error,
): Promise<T> {
  return !retries
    ? Promise.reject(previousError)
    : fn().catch(async (error) => {
        await new Promise((resolve) =>
          setTimeout(resolve, retryIntervalMillis),
        );
        return promiseRetry(fn, retryIntervalMillis, retries - 1, error);
      });
}

// TODO: this is a component, it doesn't belong here
export const highlightMatchingText = (text: string, searchTerm: string) => {
  if (!searchTerm || !text) return text;
  const searchRegex = new RegExp(`\\b${searchTerm.toLowerCase()}`, "i");
  const parts = text.split(new RegExp(`(${searchTerm})`, "i"));
  return parts.map((part, i) =>
    searchRegex.test(part) ? (
      <span key={i} className="highlight-text">
        {part}
      </span>
    ) : (
      part
    ),
  );
};

export const punctuationRegexForDefAliases = new RegExp(/[,;:|]/g);

// This is a type, it doesn't belong here
export interface HighlightDefinitionsParams {
  text: string;
  key: string;
  definitions?: DefinitionItem[];
  _case?: CaseResponse;
  clientName: string;
  docType?: DocumentSubType;
  otherParty?: Party;
}

// TODO: this is a component, it doesn't belong here
/**
 * Highlights definition terms (and their aliases) inside `text` by replacing
 * matches with React elements. Uses a two-phase approach so parenthesized
 * terms (e.g. "COLLISION(S)") are matched exactly first, then single/multi-word
 * terms and aliases are matched (longest first) to avoid partial overlaps.
 */
export const HighlightDefinitions = (
  params: HighlightDefinitionsParams,
): string | ReactNode[] => {
  const { text, key, definitions, _case, clientName, docType, otherParty } =
    params;

  if (!definitions || !_case || !docType || !otherParty) return text;

  // --- Phase 1: Parenthesized terms (exact match only) ---
  // Terms like "COLLISION(S)" are matched as-is in this pass so we don't
  // later match just "COLLISION" or "COLLISIONS" and double-highlight or miss context.
  let processedText: string | ReactNode[] = text;
  let exactMatchCounter = 0;
  definitions.forEach((def) => {
    if (def.isDeleted || !def.term) return;

    if (def.term.includes("(")) {
      const exactTerm = def.term;
      const escapedExactTerm = exactTerm.replace(
        /[-\\^$*+?.()|[\]{}]/g,
        "\\$&",
      );
      // Require word-like boundaries (start/end of string, whitespace, or punctuation).
      const exactPattern = `((?:^|\\s|[.,;:!?()])${escapedExactTerm}(?:$|\\s|[.,;:!?()]))`;
      const exactRegex = new RegExp(exactPattern, "gi");

      processedText = reactStringReplace(processedText, exactRegex, (match) => {
        const hasLeadingSpace = match[0] === " ";
        const hasTrailingSpace = match[match.length - 1] === " ";
        const trimmedMatch = match.trim();
        const uniqueIndex = exactMatchCounter++;
        // Preserve leading/trailing space as &thinsp; so layout doesn't collapse.

        return (
          <React.Fragment key={`${key}-exact-fragment-${uniqueIndex}`}>
            {hasLeadingSpace && (
              <React.Fragment key={`${key}-exact-leading-${uniqueIndex}`}>
                &thinsp;
              </React.Fragment>
            )}
            <DefinitionHighlightPropound
              key={`${key}-exact-${trimmedMatch.toLowerCase()}-${uniqueIndex}`}
              term={trimmedMatch}
              highlightKey={`${key}-exact-${trimmedMatch.toLowerCase()}-${uniqueIndex}`}
              definition={def}
              _case={_case}
              clientName={clientName}
              docType={docType}
              party={otherParty}
            />
            {hasTrailingSpace && (
              <React.Fragment key={`${key}-exact-trailing-${uniqueIndex}`}>
                &thinsp;
              </React.Fragment>
            )}
          </React.Fragment>
        );
      });
    }
  });

  /**
   * For terms with parentheses (e.g. "COLLISION(S)"), returns the full term plus
   * singular and plural forms so we can highlight "collision" and "collisions" too.
   * The full "COLLISION(S)" is already handled in Phase 1, so we skip it in allTerms.
   */
  const getTermVariations = (term: string) => {
    const variations = [term];
    const parens = term.indexOf("(");
    if (parens > -1) {
      const singular = term.substring(0, parens);
      variations.push(
        singular, // "COLLISION"
        singular.concat(term.substring(parens + 1, term.length - 1)), // "COLLISIONS"
      );
    }
    return variations;
  };

  /**
   * Builds the React fragment for one regex match: preserves leading/trailing space
   * and splits the match into prefix + highlighted term + suffix so only the actual
   * term gets the highlight (e.g. for possessive "vehicle's" we highlight "vehicle").
   */
  const CreateHighlightedElement = (
    match: string,
    index: number,
    definition: DefinitionItem,
    escapedTerm: string,
  ) => {
    const hasLeadingSpace = match[0] === " ";
    const hasTrailingSpace = match[match.length - 1] === " ";
    const trimmedMatch = match.trim();

    // escapedTerm may be multi-word (e.g. "ELECTRONIC\\s+ADDRESS"); find the term span.
    const termRegex = new RegExp(escapedTerm, "i");
    const termMatch = termRegex.exec(trimmedMatch);

    if (termMatch) {
      const termPart = termMatch[0];
      const termIndex = trimmedMatch.indexOf(termPart);
      const prefixPart = trimmedMatch.substring(0, termIndex);
      const suffixPart = trimmedMatch.substring(termIndex + termPart.length);

      return (
        <React.Fragment key={`${key}-highlight-fragment-${index}`}>
          {hasLeadingSpace && (
            <React.Fragment key={`${key}-highlight-leading-${index}`}>
              &thinsp;
            </React.Fragment>
          )}
          {prefixPart}
          <DefinitionHighlightPropound
            key={`${key}-${termPart.toLowerCase()}-${index}`}
            term={termPart}
            highlightKey={`${key}-${termPart.toLowerCase()}-${index}`}
            definition={definition}
            _case={_case}
            clientName={clientName}
            docType={docType}
            party={otherParty}
          />
          {suffixPart}
          {hasTrailingSpace && (
            <React.Fragment key={`${key}-highlight-trailing-${index}`}>
              &thinsp;
            </React.Fragment>
          )}
        </React.Fragment>
      );
    }

    return match;
  };

  // --- Build flat list of all terms + aliases for Phase 2 ---
  // Each definition contributes: term variations (singular/plural when applicable) and aliases.
  // Sort longest-first so "electronic address" is matched before "address" and we don't split highlights.
  const allTerms = definitions
    .reduce<{ term: string; definition: DefinitionItem }[]>((arr, def) => {
      if (def.isDeleted) return arr;
      getTermVariations(def.term ?? "").forEach((term) => {
        if (term.trim()) {
          if (def.term?.includes("(") && term === def.term) {
            return; // already handled in Phase 1
          }
          arr.push({ term: term.trim(), definition: def });
        }
      });
      if (def.aliases) {
        def.aliases
          .split(punctuationRegexForDefAliases)
          .map((alias) => alias.trim())
          .filter((alias) => alias)
          .forEach((alias) => {
            arr.push({ term: alias, definition: def });
          });
      }
      return arr;
    }, [])
    .sort((a, b) => b.term.length - a.term.length);

  // --- Phase 2: All term variations and aliases (longest first) ---
  // processedText may still be a string or already an array of string + React nodes from Phase 1.
  // reactStringReplace runs on each string segment, so highlights are applied across the full text.
  let generalMatchCounter = exactMatchCounter;
  return allTerms.reduce<string | ReactNode[]>(
    (currentText, { term, definition }) => {
      if (!term) return currentText;
      // Normalize so nbsp/tabs/multiple spaces still count as multi-word and match flexibly.
      const normalizedTerm = term.replace(/\s+/g, " ").trim();
      const escapedTerm = normalizedTerm.replace(
        /[-/\\^$*+?.()|[\]{}]/g,
        "\\$&",
      );
      const isMultiWord = /\s/.test(normalizedTerm);
      let pattern: string;
      let termRegexSource: string;
      if (isMultiWord) {
        // Phrase: allow flexible whitespace between words (space, nbsp, newline, etc.).
        const flexiblePhrase = escapedTerm.replace(/ /g, "\\s+");
        termRegexSource = flexiblePhrase;
        pattern = `((?:^|\\s|[.,;:!?()])${flexiblePhrase}(?:’s)?(?:'s)?(?:$|\\s|[.,;:!?()]))`;
      } else {
        // Single word: use word boundaries (\b) so we don't match substrings (e.g. "car" in "scar").
        termRegexSource = escapedTerm;
        pattern = `((?:^|\\s)\\b${escapedTerm}(?:’s)?(?:'s)?\\b(?:$|\\s|[.,;:!?()]))`;
      }

      const regex = new RegExp(pattern, "gi");
      return reactStringReplace(currentText, regex, (match) => {
        const uniqueIndex = generalMatchCounter++;
        return CreateHighlightedElement(
          match,
          uniqueIndex,
          definition,
          termRegexSource,
        );
      });
    },
    processedText,
  );
};

/**
 * Capitalizes definition terms and their aliases in a single string
 * @param inputString - The string to process
 * @param definitions - Array of definition items containing terms and aliases
 * @returns The string with capitalized terms
 */
export const capitalizeTermStr = (
  inputString: string | undefined,
  definitions: DefinitionItem[] | undefined,
): string | undefined => {
  if (!inputString || !definitions?.length) return inputString;

  const allTerms: string[] = [];

  definitions.forEach((definition) => {
    if (definition.term) {
      allTerms.push(definition.term);
    }

    if (definition.aliases) {
      const aliasList = definition.aliases
        .split(",")
        .map((alias) => alias.trim());
      allTerms.push(...aliasList);
    }
  });

  const sortedTerms = allTerms
    .filter((term) => !!term)
    .sort((a, b) => b.length - a.length);

  let result = inputString;

  sortedTerms.forEach((term) => {
    const safePattern = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`\\b${safePattern}\\b`, "gi");

    result = result.replace(regex, (match) => match.toUpperCase());
  });

  return result;
};

export const definitionRegex = new RegExp(
  /((?:[A-Z{}_]{3,}(?:\(S\))?(?:[ -]+[A-Z]{2,}(?:\(S\))?)*))/g,
);

export const excludeFromDefinitions = [
  "SSD",
  "PDA",
  "NASA",
  "NATO",
  "SWAT",
  "UNICEF",
  "UNESCO",
  "WHO",
  "OPEC",
  "FBI",
  "CIA",
  "LASER",
  "RADAR",
  "SCUBA",
  "VIP",
  "PTSD",
  "MRI",
  "DNA",
  "CPU",
  "RAM",
  "LCD",
  "LED",
  "URL",
  "HTTP",
  "HTML",
  "DUI",
  "DWI",
  "IMF",
  "GPA",
  "IBM",
  "ABS",
  "DVD",
  "USB",
  "PPE",
  "HIPAA",
  "SUV",
  "JPEG",
  "GIF",
  "SARS",
  "SIM",
  "ATM",
  "VPN",
  "DSLR",
  "DOB",
  "BCE",
  "AKA",
  "CEO",
  "CFO",
  "CPR",
  "CSI",
  "DMV",
  "FAQ",
  "FBI",
  "GDP",
  "GMO",
  "GPS",
  "HDTV",
  "ICU",
  "ISP",
  "LCD",
  "MBA",
  "NBA",
  "NFL",
  "RSVP",
  "USDA",
  "ZIP",
  "RFP",
  "STEM",
  "UFO",
  "VCR",
  "WI-FI",
  "LLC",
];

/**
 * Updates or adds a definition in the definitions array
 * @param updatedDef - The updated definition
 * @param ds - The definitions array
 * @param termToUpdate - The term to update
 * @returns Updated definitions array
 */
export const definitionsUpdater = (
  updatedDef: DefinitionItem,
  ds: DefinitionItem[] | undefined,
  termToUpdate: string,
) => {
  if (ds) {
    const existingIndex = ds.findIndex((x) => x.term === termToUpdate);
    const update = [...ds];

    if (existingIndex >= 0) {
      update[existingIndex] = updatedDef;
    } else {
      update.push(updatedDef);
    }
    return update;
  }
};

/**
 * Removes an alias from all definition items
 * @param matches - Array of definition items
 * @param aliasToRemove - The alias to remove
 * @returns Updated definition items
 */
export const getUpdatedItems = (
  matches: DefinitionItem[],
  aliasToRemove: string,
) => {
  const normalizedTerm = aliasToRemove.toUpperCase();

  return matches.map((item) => {
    const updatedItem = { ...item };
    const aliasArray = updatedItem.aliases
      ?.split(/[,;]/)
      .map((alias) => alias.trim())
      .filter((alias) => alias);

    const filteredAliases = aliasArray?.filter((alias) => {
      const normalizedAlias = alias.toUpperCase();
      return normalizedAlias !== normalizedTerm;
    });

    updatedItem.aliases =
      filteredAliases && filteredAliases?.length > 0
        ? filteredAliases?.join(", ")
        : undefined;

    return updatedItem;
  });
};

/**
 * Normalizes spaces in text by replacing multiple spaces with single spaces
 * @param text - The text to normalize
 * @returns Normalized text
 */
export const normalizeSpaces = (text: string) => {
  return text.replace(/\s+/g, " ").trim();
};

/**
 * Finds a search term within text
 * @param text - The text to search in
 * @param searches - Array of search terms
 * @returns The found search term or undefined
 */
function findWithin(text: string, searches?: string[]) {
  if (!text || !searches) return undefined;
  for (let i = 0; i < searches.length; i++) {
    const search = searches[i];

    if (text.includes(search)) {
      return search;
    }
  }

  return undefined;
}

/**
 * Extracts definitions from text and merges with existing definitions
 * @param text - The text to extract definitions from
 * @param existingDefinitions - Existing definitions array
 * @param defs - Available definitions to match against
 * @param exclude - Terms to exclude from definition matching
 * @returns Updated definitions array
 */
export const getDefinitions = (
  text: string,
  existingDefinitions: DefinitionItem[],
  defs: DefinitionItem[],
  exclude: string[],
) => {
  const update = [...existingDefinitions];
  const missing: DefinitionItem[] = [];
  const definitionMatches = [...text.matchAll(definitionRegex)];
  const toExclude = excludeFromDefinitions.concat(exclude);

  if (!definitionMatches) return [];

  for (let m = 0; m < definitionMatches.length; m++) {
    let match = definitionMatches[m][0];

    //prevents matching on substitution strings
    if (match.startsWith("{") || toExclude.find((x) => x === match)) continue;

    for (let d = 0; d < defs.length; d++) {
      const definition = defs[d];
      if (!definition.term || definition.term.length < 3) {
        continue;
      }
      const termMatch = [definition.term];

      const parens = definition.term.indexOf("(");
      if (parens > -1) {
        try {
          // Just TERM of TERM(S)
          const singular = definition.term.substring(0, parens);
          termMatch.push(singular);
          // TERMS from TERM(S)
          const plural = definition.term.substring(
            parens + 1,
            definition.term.length - 1,
          );
          termMatch.push(singular.concat(plural));
        } catch (e) {
          raygun4js("send", {
            error: e,
            message: `Term was: ${definition.term}`,
          });
        }
      }
      let allSearches: string[] = [];
      try {
        allSearches = termMatch
          .concat(
            definition.aliases
              ?.split(punctuationRegexForDefAliases)
              ?.map((x: string) => x.trim()) ?? [],
          )
          .sort((a, b) => b.length - a.length);
      } catch (e) {
        raygun4js("send", {
          error: e,
          message: `This alias failed: ${definition.aliases}`,
        });
      }
      const searchFound = findWithin(match, allSearches);
      // TODO: do we need to do (S) filtering on aliases as above?
      if (searchFound) {
        const existing = update.findIndex((x) => x.term === definition.term);
        // TODO: I can't figure out a clever way to not have duplicate code here
        if (existing === -1) {
          update.push(definition);
          //Need to potentially add definitions for terms inside other definitions
          const toAdd =
            getDefinitions(definition.content ?? "", update, defs, exclude) ??
            [];
          toAdd
            .filter((a) => !update.find((u) => u.term === a.term))
            .forEach((a) => {
              update.push(a);
            });
        } else if (!update[existing].id) {
          update[existing] = definition;
          //Need to potentially add definitions for terms inside other definitions
          const toAdd =
            getDefinitions(definition.content ?? "", update, defs, exclude) ??
            [];
          toAdd
            .filter((a) => !update.find((u) => u.term === a.term))
            .forEach((a) => {
              update.push(a);
            });
        }
        match = match.replace(searchFound, "").trim();

        if (match?.length < 3) {
          break;
        }
      }
    }

    if (match?.length >= 3) {
      missing.push({ term: match } as DefinitionItem);
    }
  }

  return update.concat(missing);
};

export function pluralize(amount: number, text: string, printNumber = false) {
  return (printNumber ? `${amount} ` : "") + (amount === 1 ? text : `${text}s`);
}
